import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, User, Share2, ArrowRight } from "lucide-react";
import api, { formatRON } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { SupplierAlerts } from "@/components/SupplierAlerts";
import { CompanyProfileEdit } from "@/components/CompanyProfileEdit";
import { LowBalanceBanner } from "@/components/LowBalanceBanner";
import { GdprPanel } from "@/components/GdprPanel";
import { ChangePassword } from "@/components/ChangePassword";
import { ProfileEditor } from "@/components/ProfileEditor";

const statusLabel = { platit: "Plătit", pending: "În așteptare" };

export default function Account() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="w-full max-w-[98%] mx-auto px-2 sm:px-3 py-3">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-12 h-12 bg-slate-900 text-white flex items-center justify-center"><User className="w-6 h-6" /></div>
        <div>
          <h1 className="text-2xl font-black tracking-tight leading-none">{user?.name}</h1>
          <p className="text-slate-500 text-sm">{user?.email}</p>
        </div>
      </div>

      <LowBalanceBanner balance={user?.tokens_balance} />

      <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-3 mt-2 flex items-center gap-2"><Package className="w-4 h-4" /> Comenzile mele</h2>

      {loading ? (
        <div className="py-16 text-center text-slate-400">Se încarcă...</div>
      ) : orders.length === 0 ? (
        <div className="border border-slate-200 p-12 text-center" data-testid="no-orders">
          <p className="text-slate-500">Nu ai nicio comandă încă.</p>
          <Link to="/produse" className="inline-block mt-4 text-orange-600 font-semibold hover:underline">Vezi produsele</Link>
        </div>
      ) : (
        <div className="space-y-2">
          {orders.map((o) => (
            <div key={o.id} className="border border-slate-200 p-4" data-testid={`order-${o.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                  <div className="text-xs text-slate-400 font-mono">#{o.id.slice(-8)}</div>
                  <div className="text-sm text-slate-500">{new Date(o.created_at).toLocaleDateString("ro-RO")}</div>
                </div>
                <Badge className={`rounded-none ${o.payment_status === "paid" ? "bg-green-600" : "bg-slate-400"}`}>
                  {o.payment_status === "paid" ? "Plătit" : "În așteptare"}
                </Badge>
              </div>
              <div className="divide-y divide-slate-100 border-t border-slate-100">
                {o.items.map((it, i) => (
                  <div key={i} className="flex justify-between py-2 text-sm">
                    <span className="text-slate-700">{it.name} × {it.quantity}</span>
                    <span className="font-semibold">{formatRON(it.price * it.quantity)}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between pt-4 mt-2 border-t border-slate-200">
                <span className="font-bold">Total</span>
                <span className="font-black text-lg">{formatRON(o.amount)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <ProfileEditor />
      <Link to="/referral" className="mt-8 flex items-center justify-between border border-orange-200 bg-orange-50/50 hover:bg-orange-50 p-5 transition-colors group" data-testid="account-referral-link">
        <div className="flex items-center gap-3">
          <Share2 className="w-6 h-6 text-orange-600" />
          <div>
            <div className="font-black text-slate-900">Program de Parteneriat</div>
            <div className="text-sm text-slate-500">Recomandă OXO și câștigă comisioane · Ajánlj és keress jutalékot</div>
          </div>
        </div>
        <ArrowRight className="w-5 h-5 text-orange-600 group-hover:translate-x-1 transition-transform" />
      </Link>
      <CompanyProfileEdit />
      <SupplierAlerts />
      <ChangePassword />
      <GdprPanel />
    </div>
  );
}
